import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, defineMessages } from 'react-intl';
import { withSiteData } from 'react-static';

import BrandButton from '../brand/BrandButton';
import Hyperlink from '../common/Hyperlink';
import Price from '../common/Price';
import Anchor from '../main/utils/Anchor';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';
import {
  GIFT_CARD_PATH,
  PLAN_ID_PP_1M,
  PLAN_ID_PPC_1M,
  PLAN_ID_PPC_12M,
  SUBSCRIPTION_PLANS,
} from '../../constants';
import radioIcon from '../../assets/images/home/plans/radio.svg';
import adsIcon from '../../assets/images/home/plans/ads.svg';
import skipIcon from '../../assets/images/home/plans/skip.svg';
import savesIcon from '../../assets/images/home/plans/saves.svg';
import waveformIcon from '../../assets/images/home/plans/waveform.svg';
import devicesIcon from '../../assets/images/home/plans/devices.svg';
import nodownloadIcon from '../../assets/images/home/plans/nodownload.svg';
import listenIcon from '../../assets/images/home/plans/listen.svg';
import playbackIcon from '../../assets/images/home/plans/playback.svg';
import unlimitedIcon from '../../assets/images/home/plans/unlimited.svg';
import libraryIcon from '../../assets/images/home/plans/library.svg';
import losslessIcon from '../../assets/images/home/plans/lossless.svg';
import castIcon from '../../assets/images/home/plans/cast.svg';
import downloadIcon from '../../assets/images/home/plans/download.svg';
import videoIcon from '../../assets/images/home/plans/video.svg';
import noteIcon from '../../assets/images/home/plans/note.svg';
import hdIcon from '../../assets/images/home/plans/hd.svg';
import studentPhoto from '../../assets/images/home/plans/student.jpg';
import giftPhoto from '../../assets/images/home/plans/gift.jpg';
import arrowIcon from '../../assets/images/home/plans/student-mark.svg';

const messages = defineMessages({
  listeningMode: {
    id: 'home.pricing.listening-mode',
    defaultMessage: 'Listening mode',
  },
  techSpecs: {
    id: 'home.pricing.tech-specs',
    defaultMessage: 'Tech specs',
  },
  freeListening1: {
    id: 'home.pricing.free.listening.1',
    defaultMessage: 'Radio-mode Streaming Only',
  },
  freeListening2: {
    id: 'home.pricing.free.listening.2',
    defaultMessage: 'Limited Skips per Hour',
  },
  freeListening3: {
    id: 'home.pricing.free.listening.3',
    defaultMessage: 'Ad Supported Listening',
  },
  freeListening4: {
    id: 'home.pricing.free.listening.4',
    defaultMessage: 'Limited Saves to Collection and Playlists',
  },
  freeTech1: {
    id: 'home.pricing.free.tech.1',
    defaultMessage: 'Standard ‘Normal’ Quality (~160 kbps)',
  },
  freeTech2: {
    id: 'home.pricing.free.tech.2',
    defaultMessage: 'No Downloads (Stream Only)',
  },
  freeTech3: {
    id: 'home.pricing.free.tech.3',
    defaultMessage: 'Listen on Mobile/Web Applications',
  },
  premiumListening1: {
    id: 'home.pricing.premium.listening.1',
    defaultMessage: 'Play any Album, Work, or Track on-demand',
  },
  premiumListening2: {
    id: 'home.pricing.premium.listening.2',
    defaultMessage: 'Unlimited Skips and full Track Selection',
  },
  premiumListening3: {
    id: 'home.pricing.premium.listening.3',
    defaultMessage: 'Ad-free, uninterrupted Playback',
  },
  premiumListening4: {
    id: 'home.pricing.premium.listening.4',
    defaultMessage: 'Unlimited Saves and Playlists',
  },
  premiumTech1: {
    id: 'home.pricing.premium.tech.1',
    defaultMessage: 'Lossless FLAC (16-bit/44.1 kHz)',
  },
  premiumTech2: {
    id: 'home.pricing.premium.tech.2',
    defaultMessage: 'Download Music for Offline Listening',
  },
  premiumTech3: {
    id: 'home.pricing.premium.tech.3',
    defaultMessage: 'Stream via any connected Devices',
  },
  videoAccess: {
    id: 'home.pricing.video.access',
    defaultMessage: 'Unlimited Video Access',
  },
  videoAccessDetail: {
    id: 'home.pricing.video.access.detail',
    defaultMessage: 'Full-length concerts, masterclasses, ballet performances, and more',
  },
  videoPlus: {
    id: 'home.pricing.video.plus',
    defaultMessage: 'Plus everything in Premium Audio',
  },
  videoTech1: {
    id: 'home.pricing.video.tech.1',
    defaultMessage: 'High-Quality Video Streaming',
  },
});

const FREE_LISTENING = [
  { icon: radioIcon, message: messages.freeListening1 },
  { icon: adsIcon, message: messages.freeListening3 },
  { icon: skipIcon, message: messages.freeListening2 },
  { icon: savesIcon, message: messages.freeListening4 },
];

const FREE_TECH = [
  { icon: waveformIcon, message: messages.freeTech1 },
  { icon: devicesIcon, message: messages.freeTech3 },
  { icon: nodownloadIcon, message: messages.freeTech2 },
];

const PREMIUM_LISTENING = [
  { icon: listenIcon, message: messages.premiumListening1 },
  { icon: playbackIcon, message: messages.premiumListening3 },
  { icon: unlimitedIcon, message: messages.premiumListening2 },
  { icon: libraryIcon, message: messages.premiumListening4 },
];

const PREMIUM_TECH = [
  { icon: losslessIcon, message: messages.premiumTech1 },
  { icon: castIcon, message: messages.premiumTech3 },
  { icon: downloadIcon, message: messages.premiumTech2 },
];

const VIDEO_LISTENING = [
  { icon: videoIcon, message: messages.videoAccess, detail: messages.videoAccessDetail },
  { icon: noteIcon, message: messages.videoPlus },
];

const VIDEO_TECH = [{ icon: hdIcon, message: messages.videoTech1 }];

const Wrapper = styled.section`
  background-color: var(--b-black);
  color: var(--b-white);
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 96px var(--b-gutter) 48px;
  text-align: center;

  ${media.large`
    height: 328px;
    padding: 152px 28px 94px;
  `};

  h2 {
    ${type('display', 'medium', { emphasis: true })};
    margin: 0;
    padding: 0;
    color: var(--b-white);

    em {
      font-style: normal;
      color: var(--b-periwinkle);
    }
  }

  p {
    ${type('headline', 'large', { emphasis: true })};
    margin: 0;
    color: var(--b-white);
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin: 0 auto;
  padding: 0 var(--b-gutter) 16px;

  ${media.large`
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows: auto 1fr;
    align-items: stretch;
    column-gap: 20px;
    row-gap: 0;
    padding: 48px;

    > article:first-child {
      grid-column: 1;
      grid-row: 2;
      height: 100%;
    }

    > article:last-child {
      grid-column: 3;
      grid-row: 2;
      height: 100%;
    }
  `};
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px 20px 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background-color: #090a09;

  a {
    border-radius: 2px;
  }

  ${media.large`
    min-height: 634px;
    border-color: transparent;
  `};

  &.featured {
    border-color: transparent;
    background-color: #0f100f;
  }
`;

const Popular = styled.div`
  padding: 0 2px 2px;
  border-radius: 16px 16px 8px 8px;
  background-color: var(--b-periwinkle);

  ${media.large`
    display: grid;
    grid-column: 2;
    grid-row: 1 / span 2;
    grid-template-rows: subgrid;

    > article {
      height: 100%;
    }
  `};

  .badge {
    ${type('label', 'small', { uppercase: true })};
    padding: 8px 16px;
    color: var(--b-black);
    text-align: center;
  }
`;

const PlanHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .name {
    ${type('headline', 'small')};
    margin: 0;
    padding: 0;
    color: var(--b-white);
  }

  .price {
    ${type('headline', 'medium', { emphasis: true })};
    margin: 4px 0 0;
    color: var(--b-white);
  }

  .note {
    ${type('body', 'small')};
    color: rgba(255, 255, 255, 0.6);
  }
`;

const Rule = styled.hr`
  width: 100%;
  height: 0;
  margin: 0;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
`;

const Lists = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 32px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  h4 {
    ${type('body', 'small', { uppercase: true })};
    margin: 0;
    padding: 0;
    color: rgba(255, 255, 255, 0.6);
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0 0 0 16px;
    list-style: none;
  }

  li {
    ${type('body', 'small')};
    display: flex;
    align-items: center;
    gap: 12px;
    color: rgba(255, 255, 255, 0.6);
  }

  li.stacked {
    align-items: flex-start;
  }

  img {
    display: block;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
  }

  small {
    ${type('detail', 'xsmall')};
    display: block;
    color: rgba(255, 255, 255, 0.48);
  }
`;

const Offers = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin: 0 auto;
  padding: 0 var(--b-gutter) 40px;

  ${media.large`
    grid-template-columns: 1fr 1fr;
    padding: 0 68px 68px;
  `};
`;

const Offer = styled(Hyperlink)`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 3px;
  background-color: #090a09;
  color: var(--b-white);
  text-decoration: none !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 2px;

  ${media.large`
    flex-direction: row;
    min-height: 212px;
    border: none;
  `};

  .arrow {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    display: block;
    width: 50px;
    height: 50px;
  }
`;

const OfferText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding: 32px 20px 20px;

  .eyebrow {
    ${type('label', 'small', { uppercase: true })};
    margin: 0 0 4px;
    color: var(--b-periwinkle);
  }

  h3 {
    ${type('headline', 'small')};
    margin: 0;
    padding: 0;
    color: var(--b-white);
  }

  p {
    ${type('body', 'small')};
    margin: 0;
    color: rgba(255, 255, 255, 0.6);
  }
`;

const Photo = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
  order: 1;

  ${media.large`
    flex: 0 1 332px;
    max-width: 48%;
    min-width: 0;
    height: auto;
    align-self: stretch;
    order: 0;
  `};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;

    ${media.large`
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 90%;
      height: 100%;
      object-position: left center;
      z-index: 1;
    `};
  }

  .orb {
    position: absolute;
    top: 50%;
    right: 16px;
    width: 42.169%;
    height: auto;
    aspect-ratio: 1;
    border-radius: 50%;
    background-color: var(--b-periwinkle);
    transform: translateY(-50%);
    display: none;

    ${media.large`
      right: 0;
      left: auto;
      display: block;
    `};
  }
`;

const GiftPhoto = styled.div`
  height: 180px;

  ${media.large`
    flex: 0 1 382px;
    max-width: 55.2%;
    min-width: 0;
    height: auto;
    align-self: stretch;
  `};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FeatureGroup = ({ title, items }) => (
  <Group>
    <h4>
      <FormattedMessage {...title} />
    </h4>
    <ul>
      {items.map(item => (
        <li key={item.message.id} className={item.detail ? 'stacked' : undefined}>
          <img src={item.icon} alt="" />
          <span>
            <FormattedMessage {...item.message} />
            {item.detail && (
              <small>
                <FormattedMessage {...item.detail} />
              </small>
            )}
          </span>
        </li>
      ))}
    </ul>
  </Group>
);

FeatureGroup.propTypes = {
  title: PropTypes.shape({
    id: PropTypes.string.isRequired,
    defaultMessage: PropTypes.string.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const planLink = (appDomain, planId) =>
  `${appDomain}/discover?fromId=lpPlans&showSubscribeModal=true&plan=${planId}`;

const trackOffer = title => () => {
  if (typeof window !== 'undefined' && window.analytics) {
    analytics.track('Clicked CTA Button', { title, from: 'Plans Section' });
  }
};

const Pricing = ({ appDomain }) => (
  <Wrapper>
    <Anchor id="plans-section" />
    <Heading>
      <h2>
        <FormattedMessage
          id="home.pricing.title"
          defaultMessage="Try {premiumFree}"
          values={{
            premiumFree: (
              <em>
                <FormattedMessage id="home.pricing.premium-free" defaultMessage="Premium Free" />
              </em>
            ),
            br: <br />,
          }}
        />
      </h2>
      <p>
        <FormattedMessage id="home.pricing.subtitle" defaultMessage="Choose how you listen" />
      </p>
    </Heading>
    <Cards>
      <Card>
        <PlanHeader>
          <h3 className="name">
            <FormattedMessage id="home.pricing.free" defaultMessage="Free" />
          </h3>
          <p className="price">
            <Price price={0} />
            <FormattedMessage id="home.pricing.per-month" defaultMessage="/mo" />
          </p>
          <div className="note">
            <FormattedMessage
              id="home.pricing.free.note"
              defaultMessage="Limited Free Access to IDAGIO"
            />
          </div>
        </PlanHeader>
        <Rule />
        <Lists>
          <FeatureGroup title={messages.listeningMode} items={FREE_LISTENING} />
          <FeatureGroup title={messages.techSpecs} items={FREE_TECH} />
        </Lists>
        <BrandButton
          to={`${appDomain}/join`}
          openInNewTab
          tone="translucent"
          size="large"
          fullWidth
          trackingData={{ title: 'Free', from: 'Plans Section' }}
        >
          <FormattedMessage
            id="home.pricing.create-free-account"
            defaultMessage="Create Free Account"
          />
        </BrandButton>
      </Card>

      <Popular>
        <div className="badge">
          <FormattedMessage id="home.pricing.most-popular" defaultMessage="Most Popular" />
        </div>
        <Card className="featured">
          <PlanHeader>
            <h3 className="name">
              <FormattedMessage
                id="index.plans-section.premium-plus.plan-title"
                defaultMessage="Premium Audio"
              />
            </h3>
            <p className="price">
              <Price price={SUBSCRIPTION_PLANS[PLAN_ID_PP_1M].monthlyPrice} />
              <FormattedMessage id="home.pricing.per-month" defaultMessage="/mo" />
            </p>
            <div className="note">
              <FormattedMessage
                id="home.pricing.audio.trial"
                defaultMessage="{price} for your first 14 Days"
                values={{ price: <Price price={0} /> }}
              />
            </div>
          </PlanHeader>
          <Rule />
          <Lists>
            <FeatureGroup title={messages.listeningMode} items={PREMIUM_LISTENING} />
            <FeatureGroup title={messages.techSpecs} items={PREMIUM_TECH} />
          </Lists>
          <BrandButton
            to={planLink(appDomain, PLAN_ID_PP_1M)}
            openInNewTab
            tone="white"
            size="large"
            fullWidth
            trackingData={{ title: 'Premium', from: 'Plans Section' }}
          >
            <FormattedMessage
              id="home.pricing.start-trial"
              defaultMessage="Start 14-Day Free Trial"
            />
          </BrandButton>
        </Card>
      </Popular>

      <Card>
        <PlanHeader>
          <h3 className="name">
            <FormattedMessage
              id="index.plans-section.premium-concerts.plan-title"
              defaultMessage="Premium Audio + Video"
            />
          </h3>
          <p className="price">
            <Price price={SUBSCRIPTION_PLANS[PLAN_ID_PPC_12M].monthlyPrice} />
            <FormattedMessage id="home.pricing.per-month" defaultMessage="/mo" />*
          </p>
          <div className="note">
            <FormattedMessage
              id="home.pricing.video.billing"
              defaultMessage="Monthly: {monthly}/mo, or *Annual: {annual}/year"
              values={{
                monthly: <Price price={SUBSCRIPTION_PLANS[PLAN_ID_PPC_1M].monthlyPrice} />,
                annual: <Price price={SUBSCRIPTION_PLANS[PLAN_ID_PPC_12M].totalPrice} />,
              }}
            />
          </div>
        </PlanHeader>
        <Rule />
        <Lists>
          <FeatureGroup title={messages.listeningMode} items={VIDEO_LISTENING} />
          <FeatureGroup title={messages.techSpecs} items={VIDEO_TECH} />
        </Lists>
        <BrandButton
          to={planLink(appDomain, PLAN_ID_PPC_12M)}
          openInNewTab
          tone="translucent"
          size="large"
          fullWidth
          trackingData={{ title: 'Premium+ Concerts', from: 'Plans Section' }}
        >
          <FormattedMessage
            id="home.pricing.start-trial"
            defaultMessage="Start 14-Day Free Trial"
          />
        </BrandButton>
      </Card>
    </Cards>

    <Offers>
      <Offer to="/students" onClick={trackOffer('Student')}>
        <Photo>
          <img src={studentPhoto} alt="" />
          <span className="orb" aria-hidden="true" />
        </Photo>
        <OfferText>
          <div>
            <p className="eyebrow">
              <FormattedMessage
                id="home.pricing.student.eyebrow"
                defaultMessage="50% off any plan"
              />
            </p>
            <h3>
              <FormattedMessage id="home.pricing.student.title" defaultMessage="Student Discount" />
            </h3>
          </div>
          <p>
            <FormattedMessage
              id="home.pricing.student.body"
              defaultMessage="For eligible Students. Same great experience, price of a good cup of coffee."
            />
          </p>
        </OfferText>
        <img className="arrow" src={arrowIcon} alt="" />
      </Offer>
      <Offer to={`/${GIFT_CARD_PATH}`} onClick={trackOffer('Gift Card')}>
        <OfferText>
          <h3>
            <FormattedMessage id="home.pricing.gift.title" defaultMessage="Gift Cards" />
          </h3>
          <p>
            <FormattedMessage
              id="home.pricing.gift.body"
              defaultMessage="Share a deeper listening experience with the music lovers in your life"
            />
          </p>
        </OfferText>
        <GiftPhoto>
          <img src={giftPhoto} alt="" />
        </GiftPhoto>
        <img className="arrow" src={arrowIcon} alt="" />
      </Offer>
    </Offers>
  </Wrapper>
);

Pricing.propTypes = {
  appDomain: PropTypes.string.isRequired,
};

export default withSiteData(Pricing);



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/Pricing.js